import { useNavigate, useSearch } from "@tanstack/react-router";
import { type FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { track } from "@/lib/analytics";
import { authClient, invalidateSession } from "@/lib/auth-client";
import { getDesktopBridge, isDesktop } from "@/lib/desktop";
import { env } from "@/lib/env";
import { rememberReferral } from "@/lib/referral";

type Mode = "signin" | "signup";

const MIN_PASSWORD_LENGTH = 8;

export function LoginPage() {
	const navigate = useNavigate();
	const { redirect, ref } = useSearch({ from: "/login" });
	const [mode, setMode] = useState<Mode>("signin");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [name, setName] = useState("");
	const [busy, setBusy] = useState<string | null>(null);
	const [magicLinkSent, setMagicLinkSent] = useState(false);
	const desktop = isDesktop();
	const [awaitingBrowser, setAwaitingBrowser] = useState(false);

	useEffect(() => {
		if (ref) {
			rememberReferral(ref);
			track("referral.captured", { from: "login" });
		}
	}, [ref]);
	let submitLabel = mode === "signin" ? "Sign in" : "Create account";
	if (busy === "password") {
		submitLabel = "Please wait…";
	}

	const finish = async () => {
		invalidateSession();
		await navigate({ to: redirect ?? "/" });
	};

	const submitPassword = async (e: FormEvent) => {
		e.preventDefault();
		setBusy("password");
		track("auth.sign_in_started", { method: "password", mode });
		const result =
			mode === "signin"
				? await authClient.signIn.email({ email, password })
				: await authClient.signUp.email({
						email,
						password,
						name: name.trim() || email.split("@")[0] || "Pluck user",
					});
		setBusy(null);
		if (result.error) {
			track("auth.sign_in_failed", {
				method: "password",
				mode,
				status: result.error.status,
			});
			toast.error(result.error.message ?? "Sign in failed");
			return;
		}
		track(mode === "signin" ? "auth.signed_in" : "auth.signed_up", {
			method: "password",
		});
		await finish();
	};

	const sendMagicLink = async () => {
		if (!email.trim()) {
			toast.error("Enter your email first");
			return;
		}
		setBusy("magic");
		track("auth.sign_in_started", { method: "magic_link", mode });
		const result = await authClient.signIn.magicLink({
			email,
			callbackURL: `${window.location.origin}/`,
		});
		setBusy(null);
		if (result.error) {
			track("auth.sign_in_failed", { method: "magic_link", mode });
			toast.error(result.error.message ?? "Could not send the link");
			return;
		}
		track("auth.magic_link_sent", { mode });
		setMagicLinkSent(true);
	};

	const social = async (provider: "github" | "google") => {
		const bridge = getDesktopBridge();
		track("auth.sign_in_started", {
			method: provider,
			mode,
			in_browser: bridge !== null,
		});
		if (bridge) {
			// OAuth can't finish inside the app window; sign in in the browser and
			// come back through a pluck://auth link (see DesktopAuthListener).
			await bridge.openExternal(
				`${env.VITE_WEB_URL}/desktop-auth?provider=${provider}`
			);
			setAwaitingBrowser(true);
			return;
		}
		setBusy(provider);
		await authClient.signIn.social(
			{ provider, callbackURL: `${window.location.origin}/` },
			{
				onError: (ctx) => {
					track("auth.sign_in_failed", { method: provider, mode });
					toast.error(ctx.error.message || ctx.error.statusText);
					setBusy(null);
				},
			}
		);
	};

	return (
		<main className="flex min-h-full items-center justify-center bg-canvas p-6">
			<Card className="w-full max-w-sm">
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<img alt="" className="size-6" src="/icon.png" />
						Pluck Studio
					</CardTitle>
					<CardDescription>
						{mode === "signin"
							? "Sign in to your library and boards."
							: "Create an account to start collecting."}
					</CardDescription>
				</CardHeader>
				<CardContent className="flex flex-col gap-4">
					{awaitingBrowser && (
						<p className="rounded-md bg-muted p-3 text-center text-muted-foreground text-sm">
							Finish signing in in your browser. Pluck Studio will continue
							automatically.
						</p>
					)}
					<div className="flex flex-col gap-2">
						<Button
							disabled={busy !== null}
							onClick={() => social("github")}
							variant="outline"
						>
							{busy === "github" ? "Redirecting…" : "Continue with GitHub"}
						</Button>
						<Button
							disabled={busy !== null}
							onClick={() => social("google")}
							variant="outline"
						>
							{busy === "google" ? "Redirecting…" : "Continue with Google"}
						</Button>
					</div>
					<div className="relative text-center text-muted-foreground text-xs uppercase">
						<span className="relative z-10 bg-card px-2">or</span>
						<div className="absolute inset-x-0 top-1/2 border-t" />
					</div>

					{magicLinkSent ? (
						<div className="flex flex-col gap-2 text-center text-sm">
							<p className="font-medium">Check your email</p>
							<p className="text-muted-foreground">
								We sent a sign-in link to {email}.
							</p>
							<Button
								onClick={() => setMagicLinkSent(false)}
								size="sm"
								variant="ghost"
							>
								Use a different method
							</Button>
						</div>
					) : (
						<form className="flex flex-col gap-3" onSubmit={submitPassword}>
							{mode === "signup" && (
								<div className="flex flex-col gap-1.5">
									<Label htmlFor="name">Name</Label>
									<Input
										autoComplete="name"
										id="name"
										onChange={(e) => setName(e.target.value)}
										value={name}
									/>
								</div>
							)}
							<div className="flex flex-col gap-1.5">
								<Label htmlFor="email">Email</Label>
								<Input
									autoComplete="email"
									id="email"
									onChange={(e) => setEmail(e.target.value)}
									placeholder="you@example.com"
									required
									type="email"
									value={email}
								/>
							</div>
							<div className="flex flex-col gap-1.5">
								<Label htmlFor="password">Password</Label>
								<Input
									autoComplete={
										mode === "signin" ? "current-password" : "new-password"
									}
									id="password"
									minLength={MIN_PASSWORD_LENGTH}
									onChange={(e) => setPassword(e.target.value)}
									required
									type="password"
									value={password}
								/>
							</div>
							<Button disabled={busy !== null} type="submit">
								{submitLabel}
							</Button>
							{!desktop && (
								<Button
									disabled={busy !== null}
									onClick={sendMagicLink}
									size="sm"
									variant="ghost"
								>
									{busy === "magic"
										? "Sending…"
										: "Email me a magic link instead"}
								</Button>
							)}
						</form>
					)}

					<p className="text-center text-muted-foreground text-xs">
						{mode === "signin" ? "New here? " : "Already have an account? "}
						<button
							className="font-medium text-foreground underline-offset-4 hover:underline"
							onClick={() => {
								const next = mode === "signin" ? "signup" : "signin";
								setMode(next);
								track("auth.mode_switched", { mode: next });
							}}
							type="button"
						>
							{mode === "signin" ? "Create an account" : "Sign in"}
						</button>
					</p>
				</CardContent>
			</Card>
		</main>
	);
}
